import { Component, Input, OnInit } from '@angular/core';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { ZipCountyStateStructure } from '../../shared/interfaces/zipCountyStateStructure.interface';
import { DocumentResponse } from '../interfaces/document-response.interface';
import { FilesByCounty } from '../interfaces/files-by-county.interface';
import { File } from '../interfaces/file.interface';
import { AuthoringModeService } from 'src/app/shared/services/authoring-mode.service';
import { ResourcesListDAMResponse } from '../../producer/resources-list/services/interfaces/ResourcesListDAM.response.interface';

interface TagPathQuery {
  docRoot: string,
  tagSearch: string,
  customTag: string,
  textsearch: string
}

interface StaticLinksParsed {
  staticLinkLabel: string;
  staticLinkUrl: string;
  staticLinkPlanDocName?: string;
}

@Component({
    selector: 'app-plan-docs-by-county',
    templateUrl: './plan-docs-by-county.component.html',
    styleUrls: ['./plan-docs-by-county.component.scss'],
    standalone: false
})
export class PlanDocsByCountyComponent implements OnInit {

  @Input() servicePath: string;
  @Input() docRoot: string = '/content/dam/';
  @Input() planTags: string;
  @Input() productHeadingDesc: string;
  @Input() planHeadingDesc: string;
  @Input() tagPathQuerys: string;
  @Input() planNames: string;
  @Input() staticLinks: string;

  tagPathQuerysParsed: Array<TagPathQuery>;
  staticLinksParsed: StaticLinksParsed[];
  county: string;
  fullPath: string;
  planList: string[] = [];
  planDocsJson: DocumentResponse[] = [];
  componentError: boolean;
  filesByCounty: FilesByCounty;
  isAuthoring = false;
  noDocumentsFound = false;
  planNamesToBeIncluded: string[] = [];

  constructor(
    private httpClient: HTTPWrapperContext,
    private authoringMode: AuthoringModeService,
    private reducerUtilityService: ReducerUtilityService
  ) { }

  ngOnInit(): void {
    this.isAuthoring = this.authoringMode.getAuthoringMode();

    if (this.planTags) {
      for (const plan of this.planTags?.split(',')) {
        this.planList.push(plan);
      }
      this.planList.sort();
    }

    if (this.planNames) {
      this.planNamesToBeIncluded = JSON.parse(this.planNames);
    }

    // Initialize headers if they are not provided
    if (!this.productHeadingDesc || this.productHeadingDesc === 'null') {
      this.productHeadingDesc = 'Product Documents'
    }
    if (!this.planHeadingDesc || this.planHeadingDesc === 'null') {
      this.planHeadingDesc = 'Plan Documents'
    }

    this.findDocuments();
  }

  async findDocuments() {
    if (this.tagPathQuerys) {
      try {
        this.tagPathQuerysParsed = JSON.parse(this.tagPathQuerys);

        if (this.tagPathQuerysParsed.length > 6) {
          this.tagPathQuerysParsed.splice(0, 6);
        }

        for (let i = 0; i < this.tagPathQuerysParsed?.length; i++) {

          const queryPath = this.createQueryPath(this.tagPathQuerysParsed[i]);
          const documents = await this.getAssetsWithTags(queryPath);
          this.planDocsJson = this.planDocsJson.concat(documents.output);

        }

        this.mapTagsToDocs(this.planDocsJson);

      }
      catch (error) {
        this.componentError = true;
        console.error(error);
      }
    }

    // Merge and sort any additional static links that were provided to the component config
    this.mergeDocumentsAndStaticLinks();
  }

  createQueryPath(tagPathQuery: TagPathQuery) {
    let queryParam = '';
    switch (tagPathQuery.tagSearch) {
      case 'county':
        const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
        if (this.isAuthoring) {
          this.county = 'Allegheny';
        } else {
          this.county = zipCountyState?.county?.county;
        }
        queryParam = 'shopx:counties/' + this.county.toLowerCase().replace(/ /g, '-');
        break;
      case 'catch-all':
        queryParam = 'shopx:shared-document';
        break;
      case 'custom':
        queryParam = tagPathQuery.customTag
        break;
      case 'textsearch':
        queryParam = tagPathQuery.textsearch
        break;
      default:
        queryParam = 'shopx:easter-egg'
    }

    let queryType = '';
    if (tagPathQuery.tagSearch === 'textsearch') {
      queryType = '&queryType=textsearch';
    }

    const queryPath = this.servicePath + '?query=' + queryParam + '&docRoot=' + tagPathQuery.docRoot + queryType;

    return queryPath;
  }

  getAssetsWithTags(queryPath: string): Promise<ResourcesListDAMResponse> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: queryPath
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.MOCK);
    return this.httpClient.makeRequest<ResourcesListDAMResponse>(requestConfig).catch(err => {
      console.error(err);
      return err;
    });
  }

  mapTagsToDocs(documents: DocumentResponse[]) {
    this.filesByCounty = {
      productDocuments: [],
      planDocuments: {}
    };

    if (documents.length === 0) {
      this.noDocumentsFound = true;
      return;
    }

    documents = documents.sort((documentA, documentB) => {
      if (documentA.title < documentB.title) { return -1; }
      if (documentA.title > documentB.title) { return 1; }
      return 0;
    });

    for (const document of documents) {
      const fileName = document.fileName.substring(0, document.fileName.lastIndexOf('.'));
      let fileTitle = document.title;

      if (!fileTitle) {
        fileTitle = fileName;
      }

      const file: File = {
        fileName: fileName,
        filePath: document.filePath,
        title: fileTitle
      };

      const documentPlanTags = this.tagIsPlanList(document.damTags);

      if (documentPlanTags.length > 0) {
        this.addFileToSpecificPlanMap(documentPlanTags, file);
      }
      else {
        this.filesByCounty.productDocuments.push(file);
      }
    }
  }

  mergeDocumentsAndStaticLinks() {
    if (this.staticLinks) {
      // Ensure we show documents even if the network did not return any since we still have static ones
      this.noDocumentsFound = false;
      this.staticLinksParsed = JSON.parse(this.staticLinks);

      this.staticLinksParsed.forEach((linkObj) => {
        // Determine which section of the document page this should be inserted into
        // filesByCounty.planDocuments is plan specific and must be identified by adding a separate name attribute
        // filesByCounty.productDocuments is the generic list at the top with no plan specific header
        if (linkObj.staticLinkPlanDocName) {
          if (!this.filesByCounty.planDocuments[linkObj.staticLinkPlanDocName]) {
            this.filesByCounty.planDocuments[linkObj.staticLinkPlanDocName] = [];
          }
          
          this.filesByCounty.planDocuments[linkObj.staticLinkPlanDocName].push({
            title: linkObj.staticLinkLabel,
            fileName: linkObj.staticLinkLabel,
            filePath: linkObj.staticLinkUrl
          });

          // Re-sort every loop so we don't have to keep track of what changed and sort later
          this.filesByCounty.planDocuments[linkObj.staticLinkPlanDocName] =
            this.filesByCounty.planDocuments[linkObj.staticLinkPlanDocName].sort((fileA, fileB) => {
              if (fileA.title < fileB.title) { return -1; }
              if (fileA.title > fileB.title) { return 1; }
              return 0;
            });
        }
        else {
          this.filesByCounty.productDocuments.push({
            title: linkObj.staticLinkLabel,
            fileName: linkObj.staticLinkLabel,
            filePath: linkObj.staticLinkUrl
          });

          // Re-sort every loop so we don't have to keep track of what changed and sort later
          this.filesByCounty.productDocuments = this.filesByCounty.productDocuments.sort((fileA, fileB) => {
            if (fileA.title < fileB.title) { return -1; }
            if (fileA.title > fileB.title) { return 1; }
            return 0;
          });
        }
      });
    }
  }

  tagIsPlanList(tags: string[]): string[] {
    const planTags = [];
    
    for (const tag of tags) {
      if (this.planList.indexOf(tag) > -1) {
        planTags.push(tag);
      }
    }

    return planTags;
  }

  addFileToSpecificPlanMap(documentPlanTags, file) {
    for (let planTag of documentPlanTags) {
      let addFile = true;

      // If only specific plan names were provided, only add files that match those plans
      if (this.planNamesToBeIncluded.length) {
        addFile = false;

        for (let planName of this.planNamesToBeIncluded) {
          if (planTag?.toLowerCase() === planName?.toLowerCase()) {
            addFile = true;
            break;
          }
        }
      }

      if (!addFile) {
        continue;
      }
      
      if (this.filesByCounty.planDocuments[planTag]) {
        this.filesByCounty.planDocuments[planTag].push(file);
      }
      else {
        this.filesByCounty.planDocuments[planTag] = [file];
      }
    }
  }
}
